import type { MessageModel, MessageTemplateModel, TemplateType } from '@/models/message.model'

type TemplateFile = { fileUrl?: string; url?: string; mimeType?: string; name?: string }
type TemplateButton = {
  id?: string
  _id?: string
  label?: string
  text?: string
  type?: string
  url?: string
  phone?: string
  value?: string
}
type TemplateCard = {
  id?: string
  fileUrl?: string
  mediaUrl?: string
  mediaType?: string
  textBody?: string
  bodyText?: string
  buttons?: TemplateButton[]
}

export type TemplateDefinition = {
  id: string
  msg?: string
  campanhaid: string
  titulo?: string
  name?: string | null
  category?: string
  templateType: string
  templateStatus?: string
  templateMessageId?: string
  dataCreate?: number
  headerMsgTemplate?: string
  footerMsgTemplate?: string
  filesWithType?: TemplateFile[]
  buttonList?: { buttons?: TemplateButton[] } | TemplateButton[] | string
  carouselCards?: TemplateCard[]
  carouselList?: TemplateCard[] | string
  optionList?: MessageModel['optionList']
  location?: MessageModel['location']
  nomeContato?: string
  telefoneContato?: string
  descricaoContato?: string
  headerFlow?: string
  bodyFlow?: string
  ctaFlow?: string
}

function parse<T>(value: T | string | undefined, fallback: T): T {
  if (typeof value !== 'string') return value || fallback
  try {
    return JSON.parse(value) as T
  } catch {
    return fallback
  }
}

function buttons(values: TemplateButton[] = []) {
  if (!Array.isArray(values)) return []
  return values
    .filter(button => button && typeof button === 'object')
    .map((button, index) => ({
      id: button.id || button._id || `preview-button-${index}`,
      label: button.label || button.text || '',
      type: (button.type || 'REPLY') as MessageModel['buttonContent']['buttons'][number]['type'],
      url: /^https?:\/\//i.test(button.url || '') ? button.url || '' : '',
      phone: button.phone || '',
      value: button.value
    }))
}

export function toMessageTemplateModel(message: TemplateDefinition): MessageTemplateModel {
  const body = message.msg?.trim() ? message.msg : message.bodyFlow || ''
  const callPermission = /^CALL_PERMISSION_REQUEST_/.test(message.templateType || '')
  const main: Partial<MessageModel> = {
    messageId: `preview-${message.id}`,
    messageText: body,
    fromMe: true,
    status: 'sent',
    messageTypeSent: message.templateType as TemplateType,
    optionList: message.optionList,
    location: message.location
  }
  const rawButtons = parse(message.buttonList, { buttons: [] })
  const actions = callPermission ? [] : buttons(Array.isArray(rawButtons) ? rawButtons : rawButtons?.buttons)
  if (message.ctaFlow && !actions.length) actions.push(...buttons([{ label: message.ctaFlow, type: 'FLOW' }]))
  const parsedCards = message.carouselCards?.length
    ? message.carouselCards
    : parse<TemplateCard[]>(message.carouselList, [])
  const cards = Array.isArray(parsedCards) ? parsedCards.filter(card => card && typeof card === 'object') : []
  if (cards.length) {
    main.carouselContent = cards.map((card, index) => ({
      buttons: buttons(card.buttons).map(button => ({ ...button, _id: button.id })),
      fileUrl: card.fileUrl || card.mediaUrl || '',
      mimeType: card.mediaType?.toLowerCase().includes('video') ? 'video/mp4' : 'image/jpeg',
      textBody: card.bodyText || card.textBody || '',
      messageParameters: { textBody: card.bodyText || card.textBody || '' },
      buttonsParameter: [],
      fileHash: card.id || String(index)
    }))
  }
  const files = Array.isArray(message.filesWithType) ? message.filesWithType : []
  const media = files
    .filter(file => file && /^(https?:\/\/)/i.test(file.fileUrl || file.url || ''))
    .map(file => {
      const url = file.fileUrl || file.url || ''
      const mime = file.mimeType || ''
      if (mime.startsWith('image/')) return { image: { imageUrl: url, mimeType: mime } }
      if (mime.startsWith('video/')) return { video: { videoUrl: url, mimeType: mime } }
      if (mime.startsWith('audio/')) return { audio: { audioUrl: url, mimeType: mime } }
      return { document: { documentUrl: url, mimeType: mime, fileName: file.name || '' } }
    })
  if (actions.length && !cards.length) {
    main.buttonContent = {
      msgHeader: message.headerMsgTemplate || message.headerFlow,
      msgText: body,
      msgFooter: message.footerMsgTemplate,
      buttons: actions
    }
    main.messageText = ''
  }
  if (message.telefoneContato) {
    main.contactsMessage = [
      {
        name: { formatted_name: message.nomeContato || message.telefoneContato, description: message.descricaoContato },
        phones: [{ phone: message.telefoneContato }]
      }
    ]
  }
  // Keep media as separate preview entries so captions/body never repeat.
  const previewMessages: Array<Partial<MessageModel>> = callPermission
    ? [
        ...(body ? [{ ...main, messageTypeSent: undefined }] : []),
        { ...main, messageId: `${main.messageId}-permission`, messageText: '' }
      ]
    : cards.length
    ? [main]
    : [
        ...media.map((item, index) => ({
          ...main,
          ...item,
          messageId: `${main.messageId}-media-${index}`,
          messageText: '',
          buttonContent: undefined,
          optionList: undefined,
          contactsMessage: undefined,
          location: undefined
        })),
        main
      ]
  return {
    id: message.id,
    templateId: message.templateMessageId || '',
    messageId: message.id,
    name: message.name?.trim() || message.titulo?.trim() || '',
    category: message.category || '',
    campaignId: message.campanhaid,
    status: message.templateStatus || '',
    typeSent: message.templateType as TemplateType,
    components: [{ type: 'BODY', text: body }],
    dateCreated: message.dataCreate || 0,
    preview: {
      messages: previewMessages,
      contentUnavailable:
        !body.trim() &&
        !media.length &&
        !cards.length &&
        !main.optionList &&
        !main.contactsMessage &&
        !main.location &&
        !callPermission,
      header: actions.length ? '' : message.headerMsgTemplate || message.headerFlow,
      footer: actions.length ? '' : message.footerMsgTemplate,
      mediaUnavailable:
        !cards.length &&
        /COM_MIDIA|^SOMENTE_MIDIA|^ONLY_(IMAGE|VIDEO|AUDIO)$/.test(message.templateType) &&
        !media.length
    }
  }
}
